import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ArrowLeft, Loader2, Building2, Save, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { toast } from "sonner";

export default function CompanyFormPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    net: 30,
    domain: "",
    start_date: "",
    contact: "",
    email: "",
  });

  useEffect(() => {
    if (isEdit) {
      fetchCompany();
    } else {
      // Set default start date to today
      const today = new Date();
      setDate(today);
      setFormData(prev => ({ ...prev, start_date: format(today, "yyyy-MM-dd") }));
    }
  }, [id]);

  const fetchCompany = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/companies/${id}`);
      setFormData(response.data);
      if (response.data.start_date) {
        setDate(new Date(response.data.start_date));
      }
    } catch (error) {
      toast.error("Failed to fetch company");
      navigate("/companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/companies/${id}`, formData);
        toast.success("Company updated successfully");
      } else {
        await api.post("/companies", formData);
        toast.success("Company created successfully");
      }
      navigate("/companies");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      updateField("start_date", format(selectedDate, "yyyy-MM-dd"));
    } else {
      updateField("start_date", "");
    }
    setCalendarOpen(false); // Close popover after selection
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/companies")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-8 w-8 text-accent" />
            {isEdit ? "Edit Company" : "Create Company"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Update company information" : "Add a new partner company"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
            <CardDescription>Enter the company details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder="Enter company name"
                  data-testid="company-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="net">NET (days)</Label>
                <Input
                  id="net"
                  type="number"
                  value={formData.net}
                  onChange={(e) => updateField("net", parseInt(e.target.value) || 30)}
                  placeholder="30"
                  data-testid="company-net-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="contact@company.com"
                  data-testid="company-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="domain">Domain</Label>
                <Input
                  id="domain"
                  value={formData.domain}
                  onChange={(e) => updateField("domain", e.target.value)}
                  placeholder="company.com"
                  data-testid="company-domain-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact">Contact Person</Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => updateField("contact", e.target.value)}
                  placeholder="John Doe"
                  data-testid="company-contact-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                      data-testid="company-start-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-50" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate("/companies")}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-company-btn">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? "Update Company" : "Create Company"}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
